'use client';

import React, { useEffect, useState } from 'react';
import { usePathname, useRouter, useSearchParams } from 'next/navigation';
import Image from 'next/image';
import { Button, Checkbox, Form } from 'antd';

import LayoutDashboard from '@/components/Dashboard';
import Highlight from '@/components/ui/Highlight';
import { notificactionWarning } from '@/components/ui/notificaction';

import { useStore } from '@/store/zustand-solicitud';
import { getEntidad } from '@/utils/services/entidad';
import solicitud from '@/models/solicitud';
import { openBase64Pdf } from '@/utils/services/files';

import classes from './page.module.scss';
import OnboardingDefault from '@/components/Onboarding/OnboardingDefault';
import OnboardingVirginia from '@/components/Onboarding/OnboardingVirginia';
import OnboardingRepresentanteLegal from '@/components/Onboarding/OnboardingRepresentanteLegal';
import { VistaPrevia } from '@/utils/services/vistaPreviaRL';
import { CLAVE_MODO_ASISTIDO } from '@/utils/services/modoAsistido';

const Root = () => {
  const [form] = Form.useForm<any>();
  const router = useRouter();
  const pathname = usePathname();
  const search = useSearchParams();

  const entidadId = search.get('e');
  const type = search.get('t');
  const ref_id = search.get('ref_id');
  const solicitudJuridica = search.get('s');
  const tipoEntidad = search.get('tE');

  // Modo asistido: el onboarding lo opera un asistente y no el cliente final (AB#10758). Entra por
  // el link del operador y no por una senal derivada porque hoy no existe ninguna: el front no
  // distingue "juridico asistido" de "juridico autoasistido" (gap DEV-BO-12). Cuando esa senal
  // exista -flag de entidad o ingesta de sucursal/usuario, AB#10734- se reemplaza aca sin tocar
  // las pantallas.
  const modoAsistido = search.get('a') === '1';

  // El link de invitacion del representante legal lo arma el backend como '?e=...&s=...', sin 't'
  // (SendEmailsRLCommand / ReSendEmailRLCommand). La condicion es la misma que ya usa setSteps para
  // decidir si el paso /conformidad-rl le corresponde: solicitud juridica presente y flujo que no
  // es el juridico.
  const esRepresentanteLegal = !!solicitudJuridica && type !== 'juridico';

  // La aceptacion de terminos no se pide en la bienvenida de la persona juridica, en ningun modo.
  // No es una cuestion de quien opera la pantalla: en el flujo juridico quien tiene que aceptar es
  // el representante legal, y lo hace en su propia pantalla con autoria y fecha (RF-ONB-19). El
  // check acá no registraba nada -era un gate local- y pedirlo igual sugiere una aceptacion que no
  // existe. La entidad ya saco update-acepta-tyc del Flujo(PersonaJuridica).
  //
  // El caso asistido se mantiene para persona fisica, que sigue aceptando aca cuando carga sola.
  const ocultarTerminos = type === 'juridico' || modoAsistido;

  const { setColor, setType, setNavbarVisible, setEntidadId, setSteps, moveStep, resetStore, setLogo, logo } = useStore();

  const [tyc, setTyc] = useState<string>();
  const [vistaPrevia, setVistaPrevia] = useState<VistaPrevia | null>(null);
  const [loading, setLoading] = useState(true);
  const openTyC = () => openBase64Pdf(tyc ?? '');

  useEffect(() => {
    const fetchData = async () => {
      if (!entidadId) {
        router.replace('/404');
        return;
      }

      // Limpiar almacenamiento y store
      resetStore();
      localStorage.clear();
      sessionStorage.clear();

      try {
        sessionStorage.setItem('ref_id', ref_id ?? '');
        sessionStorage.setItem('type', type ?? '');
        sessionStorage.setItem('solicitudJ', solicitudJuridica ?? '');
        sessionStorage.setItem('entidadId', entidadId);
        sessionStorage.setItem('tipoEntidad', tipoEntidad ?? '');
        // Lo leen las pantallas de error para poder devolver al operador al mismo modo del que
        // salio; ver sufijoModoAsistido.
        sessionStorage.setItem(CLAVE_MODO_ASISTIDO, modoAsistido ? '1' : '');

        const entidad = await getEntidad(entidadId);

        setColor(entidad.color);
        setLogo(entidad.logo);
        setType(type);
        setSteps(entidad);
        setNavbarVisible(false);
        setEntidadId(entidadId);

        const tycData = await solicitud.getTyC();
        setTyc(tycData.contenido);

        // Se pide aca dentro y no en el componente para que lo cubra el 'loading' que ya existe:
        // asi la pantalla aparece una sola vez, con los datos puestos, y no vacia y despues llena.
        // getVistaPreviaJuridica lee 'solicitudJ', que quedo guardado unas lineas mas arriba.
        if (esRepresentanteLegal) {
          const datos = await solicitud.getVistaPreviaJuridica();
          setVistaPrevia(datos || null);
        }
      } catch (error) {
        console.error('Error al obtener datos:', error);
        router.replace('/404');
      } finally {
        setLoading(false);
      }
    };

    fetchData();
  }, []);

  const handleSubmit = async () => {
    const { tyc } = form.getFieldsValue();

    // Misma condicion que el render, y por eso vive en una sola variable: si el check no esta en
    // pantalla, exigirlo deja el boton Iniciar bloqueado por algo que el usuario no puede hacer.
    if (!ocultarTerminos && !tyc) {
      notificactionWarning('Por favor, acepte los términos y condiciones para continuar');
      return;
    }

    const next = await moveStep(pathname, router, 'next');
    if (next) return;
  };

  // La bienvenida del representante legal solo muestra los datos y avanza (AB#10817). La
  // conformidad con autoria y fecha se registra en /conformidad-rl, que es el paso creado para eso:
  // ahi elige cual de los representantes declarados es, y esa autoria es lo que se persiste. Tener
  // el registro tambien aca obligaba a mantener dos versiones del mismo acto.
  const handleSubmitRepresentanteLegal = async () => {
    await moveStep(pathname, router, 'next');
  };

  if (loading) return null;

  // El saludo se repite del componente de cada entidad para que el representante legal vea el
  // mismo que veria el titular. Es la unica parte de esas pantallas que la suya reusa.
  const getTituloByTipoEntidad = (tipo: string | null) => {
    switch (tipo) {
      case 'virginia':
        return (
          <>
            ¡Bienvenido a <Highlight> Mundo Virginia!</Highlight>
          </>
        );
      default:
        return (
          <>
            Te damos la bienvenida a <Highlight> Onboarding </Highlight>
          </>
        );
    }
  };

  const getComponentByTipoEntidad = (tipo: string | null) => {
    if (esRepresentanteLegal) {
      return (
        <OnboardingRepresentanteLegal
          logo={logo}
          titulo={getTituloByTipoEntidad(tipo)}
          vistaPrevia={vistaPrevia}
          onIniciar={handleSubmitRepresentanteLegal}
          openTyC={openTyC}
        />
      );
    }

    switch (tipo) {
      case 'virginia':
        return (
          <OnboardingVirginia
            form={form}
            logo={logo}
            tyc={tyc ?? ''}
            handleSubmit={handleSubmit}
            openTyC={openTyC}
            type={type ?? undefined}
            ocultarTerminos={ocultarTerminos}
          />
        );
      default:
        return (
          <OnboardingDefault
            form={form}
            logo={logo}
            tyc={tyc ?? ''}
            handleSubmit={handleSubmit}
            openTyC={openTyC}
            type={type ?? undefined}
            ocultarTerminos={ocultarTerminos}
          />
        );
    }
  };

  return <LayoutDashboard directionContent="center">{getComponentByTipoEntidad(tipoEntidad)}</LayoutDashboard>;
};

export default Root;
